import { LightBulbIcon } from '@heroicons/react/outline';
import ContainerWithBorder from 'components/atoms/ContainerWithBorder';
import MoneyCounter from 'components/atoms/MoneyCounter';
import GivingCoinsIcon from 'components/atoms/SVG/GivingCoinsIcon.component';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { type YearlyReviewData } from 'types/global.types';
import { getReviewYears } from '../helpers';
import PercentageLabel from '../PercentageLabel';

const EarningsSection: FC<{ data?: YearlyReviewData }> = ({ data }) => {
  const { t } = useTranslation();

  const { thisYear, lastYear } = data || {};

  const lastYearTurnover = lastYear?.turnover?.total || 0;
  const thisYearTurnover = thisYear?.turnover?.total || 0;

  const thisYearNetEarnings = thisYear?.netEarnings?.afterTax || 0;
  const lastYearNetEarnings = lastYear?.netEarnings?.afterTax || 0;

  const gainRelevantToLastYearPercentage =
    ((thisYearTurnover - lastYearTurnover) / lastYearTurnover) * 100;

  const earningsGainOrLossPercentage =
    ((thisYearNetEarnings - lastYearNetEarnings) / lastYearNetEarnings) * 100;

  const { year, previousYear } = getReviewYears();

  return (
    <section className="grid grid-cols-2 gap-5">
      <ContainerWithBorder
        containerClassName="relative overflow-hidden"
        className="h-[50vh] p-6"
      >
        <div className="p-6">
          <div className="flex items-center space-x-2 mb-10">
            <GivingCoinsIcon className="w-10 h-10 text-revenues" />

            <h1 className="font-semibold text-xl text-primary">
              {`${t('yearly_review.total_revenue_widget.title')} (${t(
                'yearly_review.exc_vat',
              )})`}
            </h1>
          </div>
          <div className="flex flex-col items-center flex-1 h-full pt-10 pb-0">
            <div className="w-full text-center">
              <div className="font-avenir text-4xl mb-3 font-semibold">
                <MoneyCounter
                  amount={thisYearTurnover}
                  currency="EUR"
                  stateId="yearlyReviewRevenuesTotal"
                  noFractions
                />
              </div>

              <PercentageLabel
                value={gainRelevantToLastYearPercentage}
                comparisonLabel={previousYear}
              />
            </div>

            <div className="w-full mt-5 flex flex-col flex-1 justify-center font-semibold text-md text-primary">
              <div className="flex items-center justify-between border-b border-primary-50 py-3">
                <span>{t('yearly_review.total_revenue_widget.clients')}</span>

                <span>{thisYear?.clients?.count || '-'}</span>
              </div>

              <div className="flex items-center justify-between border-b border-primary-50 py-3">
                <span>{t('yearly_review.total_revenue_widget.Invoices')}</span>

                <span>{thisYear?.invoices?.totalCount?.total || '-'}</span>
              </div>

              <div className="flex items-center justify-between pt-3">
                <span>
                  {t('yearly_review.total_revenue_widget.time_to_get_paid')}
                </span>

                <span>
                  {thisYear?.paymentTerms?.mean
                    ? Math.floor(thisYear?.paymentTerms?.mean)
                    : '-'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </ContainerWithBorder>

      <ContainerWithBorder
        containerClassName="relative overflow-hidden"
        className="p-6"
      >
        <div className="p-6 h-full flex flex-col">
          <div className="flex items-center space-x-2 mb-10">
            <GivingCoinsIcon className="w-10 h-10 text-taxes" />

            <h1 className="font-semibold text-xl text-primary">
              {thisYearNetEarnings < 0
                ? t('yearly_review.profit_widget.loss_title')
                : t('yearly_review.profit_widget.earnings_title')}
            </h1>
          </div>
          <div className="flex flex-col items-center flex-1 h-full pt-10 pb-0">
            <div className="w-full text-center">
              <div className="font-avenir text-4xl mb-3 font-semibold">
                <MoneyCounter
                  amount={thisYearNetEarnings}
                  currency="EUR"
                  stateId="yearlyReviewTotalEarnings"
                  noFractions
                />
              </div>

              <PercentageLabel
                value={earningsGainOrLossPercentage}
                comparisonLabel={previousYear}
              />
            </div>

            <div className="w-full mt-5 flex flex-col flex-1 justify-center">
              <div className="flex items-start bg-primary-25 rounded-lg p-4 border border-neutral-25">
                <LightBulbIcon className="w-16 mr-4 text-primary-700 mt-2" />

                <p className="text-primary-700 text-sm">
                  {t('yearly_review.profit_widget.alert_text', {
                    year,
                  })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </ContainerWithBorder>
    </section>
  );
};

export default EarningsSection;
